import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Toolbar,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Paper,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { Add, Refresh, Edit } from "@mui/icons-material";

import HeaderBar from "../../components/HeaderBar";
import LeftDrawer from "../../components/LeftDrawer";
import { TableSkeleton } from "../../components/common/SkeletonLoader";
import { useToast } from "../../components/common/ToastProvider";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import EnhancedTable from "../../components/common/EnhancedTable";
import {
  createTermDeposits,
  fetchTermDeposits,
} from "../../redux/masters/termDepositSlice";
import { getOptions, mergeOptions } from "../../redux/masters/optionSlice";
import { OptionsStatus } from "../../constants/enum";
import AddDepositAccountModal from "../../components/modals/AddDepositAccountModal";
import { convertTermDepositsToOptionData } from "../../utils/optionUtils";
import { fetchTermDepositAccounts } from "../../redux/loandeposits/depositAccountSlice";

const DepositAccounts = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const {
    items: termDepositAccounts,
    loading,
    error
  } = useSelector((state) => state.termDepositAccounts);
  const {
    data: depositOptions = {},
    loading: optionLoading,
    error: optionError,
  } = useSelector((state) => state.option || {});

  const optionKey = OptionsStatus.DepositAccounts;
  const optionData = depositOptions[optionKey] || [];

  const { showSuccess, showError } = useToast();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editingDepositAccountId, setEditingDepositAccountId] = useState(null);
  const [addLoading, setAddLoading] = useState(false);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerClose = () => setMobileOpen(false);
  const handleCloseModal = () => setOpenModal(false);

  useEffect(() => {
    dispatch(fetchTermDepositAccounts());
  }, [dispatch]);

  const handleAddNew = async () => {
    try {
      setAddLoading(true);
      await ensureDepositAccountsOptionLoaded();
      setEditData(null);
      setOpenModal(true);
    } catch (err) {
      console.log('Error:',err);
      showError("Failed to load loan options");
    } finally {
      setAddLoading(false);
    }
  };

  const ensureDepositAccountsOptionLoaded = async () => {
    if (!optionData || optionData.length === 0) {
      try {
        await dispatch(getOptions([optionKey])).unwrap();
        await dispatch(fetchTermDepositAccounts()).unwrap();
        const termDeposits = await dispatch(fetchTermDeposits()).unwrap();
        const termDepositOptions = convertTermDepositsToOptionData(termDeposits);
        dispatch(mergeOptions(termDepositOptions));
      } catch (err) {
        showError("Failed to load options");
        throw err;
      }
    }else {
      const termDeposits = await dispatch(fetchTermDeposits()).unwrap();
      dispatch(mergeOptions(convertTermDepositsToOptionData(termDeposits)));
    }
  };

  const handleRefresh = async () => {
    try {
      await dispatch(fetchTermDepositAccounts()).unwrap();
      showSuccess("Data refreshed successfully");
    } catch (error) {
      showError(error.message || "Failed to refresh data");
    }
  };

  const handleSaveLoanType = async (form) => {
    try {
      await dispatch(createTermDeposits(form)).unwrap();
      await dispatch(fetchTermDepositAccounts()).unwrap();
      showSuccess("Loan type created successfully");
      handleCloseModal();
    } catch (error) {
      showError(error.message || "Failed to create loan type");
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <HeaderBar onMobileMenuToggle={handleDrawerToggle} />
      <LeftDrawer
        activeController="/loan-deposit/term-deposit"
        mobileOpen={mobileOpen}
        onMobileClose={handleDrawerClose}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: "background.default",
          p: { xs: 1, sm: 2, md: 3 },
          minHeight: "100vh",
          width: { sm: `calc(100% - ${isMobile ? 0 : 240}px)` },
        }}
      >
        <AddDepositAccountModal
          optionData={optionData}
          optionLoading={optionLoading}
          optionError={optionError}
          open={openModal}
          onClose={() => {
            setOpenModal(false);
            setEditData(null);
          }}
          onSave={handleSaveLoanType}
          data={editData} // pass selected item to modal
        />

        <Toolbar sx={{ minHeight: 64 }} />
        <Breadcrumbs />

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardHeader
            sx={{
              px: { xs: 2, sm: 3 },
              py: 2,
              backgroundColor: "background.secondary",
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
            title={
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: "1rem", sm: "1.125rem" },
                  fontWeight: 600,
                  color: "text.primary",
                }}
              >
                Term Deposit Accounts
              </Typography>
            }
            action={
              <Box sx={{ display: "flex", gap: 1 }}>
                <Tooltip title="Refresh Data">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    sx={{ display: { xs: "inline-flex", sm: "none" } }}
                  >
                    <Refresh />
                  </IconButton>
                </Tooltip>

                <Button
                  variant="contained"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleAddNew}
                  startIcon={
                    addLoading ? <CircularProgress size={16} /> : <Add />
                  }
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                  disabled={addLoading}
                >
                  {isMobile ? "Add" : "Add New"}
                </Button>

                {!isMobile && (
                  <Button
                    variant="outlined"
                    size="medium"
                    onClick={handleRefresh}
                    startIcon={<Refresh />}
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 500,
                    }}
                  >
                    Refresh
                  </Button>
                )}
              </Box>
            }
          />
          <CardContent sx={{ p: { xs: 1, sm: 2 } }}>
            {loading ? (
              <TableSkeleton rows={5} columns={13} />
            ) : error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {error}
              </Alert>
            ) : (
              <EnhancedTable
                data={termDepositAccounts || []}
                columns={[
                  {
                    id: "depositCode",
                    label: "Code",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositName",
                    label: "Name",
                    sortable: true,
                    render: (value) => value,
                  },
                  {
                    id: "depositType",
                    label: "Deposit Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "depositFrequency",
                    label: "Frequency",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestRate",
                    label: "ROI (%)",
                    sortable: true,
                    render: (value) => `${value}%`,
                    hidden: isMobile,
                  },
                  {
                    id: "interestFrequency",
                    label: "Int. Credit",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "interestType",
                    label: "Int Type",
                    sortable: true,
                    hidden: isMobile,
                  },
                  {
                    id: "active",
                    label: "Status",
                    sortable: true,
                    render: (value) =>
                      value ? (
                        <Chip label="Active" size="small" color="success" />
                      ) : (
                        <Chip
                          label="Inactive"
                          size="small"
                          variant="outlined"
                        />
                      ),
                  },
                  {
                    id: "editable",
                    label: "*",
                    sortable: false,
                    render: (_, row) => (
                      <Tooltip
                        title={
                          row.editable
                            ? "Edit Term Deposit"
                            : "Data is not editable"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !row.editable || editingDepositAccountId !== null
                            }
                            onClick={async (e) => {
                              e.stopPropagation();
                              setEditingDepositAccountId(row.id);
                              try {
                                await ensureLoanOptionsLoaded();
                                setEditData(row);
                                setOpenModal(true);
                              } catch (err) {
                                // handle error
                              } finally {
                                setEditingDepositAccountId(null);
                              }
                            }}
                          >
                            {editingDepositAccountId === row.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              <Edit fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ),
                  },
                ]}
                title="Term Deposits"
                loading={loading}
                emptyMessage="No term deposits found"
                stickyHeader={true}
                dense={true}
                showRowNumbers={true}
              />
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default DepositAccounts;
